import type React from 'react';
import { colors } from '@this/shared/ui/theme/tokens/primitive-tokens/colors';
import { getBaseStyles } from './baseStyles';

// User側とAdmin側の共通スタイルを定義
export function getCommonStyles() {
  const baseStyles = getBaseStyles();

  return {
    // baseStylesの基本要素をスプレッド
    ...baseStyles,

    // モーダル関連（User/Admin共通）
    modal: {
      backdrop: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      } as React.CSSProperties,
      container: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto' as const
      } as React.CSSProperties,
      header: {
        fontSize: '18px',
        fontWeight: 'bold',
        marginBottom: '16px'
      } as React.CSSProperties,
      body: {
        marginBottom: '24px'
      } as React.CSSProperties,
      footer: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end'
      } as React.CSSProperties,
      title: {
        fontSize: '20px',
        fontWeight: 'bold',
        marginBottom: '12px',
        textAlign: 'center' as const
      } as React.CSSProperties,
      message: {
        fontSize: '16px',
        textAlign: 'center' as const
      } as React.CSSProperties,
      error: {
        color: colors.red[600],
        fontSize: '18px',
        fontWeight: 'bold',
        marginBottom: '8px',
        textAlign: 'center' as const
      } as React.CSSProperties,
      content: {
        textAlign: 'center' as const,
        padding: '40px 20px'
      } as React.CSSProperties
    },

    // エラー/成功/警告メッセージ（共通）
    message: {
      error: {
        container: {
          backgroundColor: colors.red[50],
          borderRadius: '4px',
          padding: '12px 16px',
          marginBottom: '24px'
        } as React.CSSProperties,
        text: {
          color: colors.red[700],
          fontSize: '14px'
        } as React.CSSProperties
      },
      success: {
        container: {
          backgroundColor: colors.green[50],
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '32px',
          textAlign: 'center' as const
        } as React.CSSProperties,
        title: {
          fontSize: '20px',
          fontWeight: 'bold',
          marginBottom: '12px',
          color: colors.green[700]
        } as React.CSSProperties,
        text: {
          fontSize: '14px',
          color: colors.gray[600]
        } as React.CSSProperties
      },
      confirm: {
        container: {
          backgroundColor: colors.gray[50],
          borderRadius: '8px',
          padding: '16px',
          marginTop: '24px',
          marginBottom: '24px'
        } as React.CSSProperties,
        text: {
          color: colors.gray[600],
          fontSize: '14px'
        } as React.CSSProperties
      },
      warning: {
        container: {
          border: `1px solid ${colors.yellow[200]}`,
          borderRadius: '8px',
          padding: '16px',
          marginTop: '24px',
          marginBottom: '24px'
        } as React.CSSProperties,
        text: {
          color: colors.yellow[700]
        } as React.CSSProperties
      }
    },

    // ステータスバッジ（詳細版）
    status: {
      ...baseStyles.status,
      draft: {
        ...baseStyles.status.badge,
        backgroundColor: colors.gray[100],
        color: colors.gray[700]
      } as React.CSSProperties,
      admin_applied: {
        ...baseStyles.status.badge,
        backgroundColor: colors.blue[100],
        color: colors.blue[700]
      } as React.CSSProperties,
      first_approved: {
        ...baseStyles.status.badge,
        backgroundColor: colors.green[100],
        color: colors.green[700]
      } as React.CSSProperties,
      final_approved: {
        ...baseStyles.status.badge,
        backgroundColor: colors.green[100],
        color: colors.green[700]
      } as React.CSSProperties,
      user_applied: {
        ...baseStyles.status.badge,
        backgroundColor: colors.blue[100],
        color: colors.blue[700]
      } as React.CSSProperties,
      sent_back: {
        ...baseStyles.status.badge,
        backgroundColor: colors.yellow[100],
        color: colors.yellow[700]
      } as React.CSSProperties,
      rejected: {
        ...baseStyles.status.badge,
        backgroundColor: colors.red[100],
        color: colors.red[700]
      } as React.CSSProperties
    },

    // フォーム入力関連（共通）
    form: {
      input: {
        base: {
          width: '100%',
          maxWidth: '600px'
        } as React.CSSProperties,
        wrapper: {
          marginBottom: '14px'
        } as React.CSSProperties,
        error: {
          borderColor: colors.red[500]
        } as React.CSSProperties
      },
      label: {
        base: {
          fontSize: '14px',
          fontWeight: 'bold',
          marginBottom: '8px',
          display: 'block'
        } as React.CSSProperties,
        required: {
          color: colors.red[500],
          marginLeft: '4px'
        } as React.CSSProperties
      },
      errorMessage: {
        color: colors.red[600],
        fontSize: '12px',
        marginTop: '4px',
        marginBottom: '0'
      } as React.CSSProperties,
      helperText: {
        fontSize: '12px',
        color: colors.gray[600],
        marginTop: '4px'
      } as React.CSSProperties
    },

    // ボタン関連（共通）
    button: {
      group: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end'
      } as React.CSSProperties,
      primary: {
        padding: '8px 16px',
        fontSize: '14px'
      } as React.CSSProperties,
      secondary: {
        padding: '6px 12px',
        fontSize: '14px'
      } as React.CSSProperties
    },

    // コンテナ（共通）
    container: {
      main: {
        backgroundColor: colors.white,
        width: '90%',
        maxWidth: '1440px',
        // margin: '20px auto',
        margin: '0 auto',
        padding: '20px'
      } as React.CSSProperties
    },

    // 合計金額表示（共通）
    total: {
      container: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: colors.gray[20],
        borderTop: `1px solid ${colors.gray[200]}`,
        borderBottom: `1px solid ${colors.gray[200]}`,
        fontWeight: 'bold'
      } as React.CSSProperties
    },

    // タイトル関連（共通）
    title: {
      section: {
        // fontSize: '18px',
        fontSize: '16px',
        fontWeight: 'bold',
        backgroundColor: 'lightblue',
        marginBottom: '16px'
      } as React.CSSProperties,
      fullWidth: {
        margin: 0,
        fontSize: '30px',
        backgroundColor: 'blue',
        marginBottom: '30px'
      } as React.CSSProperties
    },

    // 固定フッター（共通）
    fixedFooter: {
      container: {
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderTop: `1px solid ${colors.gray[200]}`,
        padding: '16px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 100
      } as React.CSSProperties
    },

    // アクション関連（共通）
    actions: {
      container: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end'
      } as React.CSSProperties
    }
  };
}

export const commonStyles = getCommonStyles();
